// Skeleton — shimmer-friendly placeholder slabs that share the canonical
// ``alm-card`` palette so loading states no longer drift between
// surfaces (plan §P2.20 — UI Workflow Consistency Audit).
//
// Before this primitive landed every surface picked its own affordance:
//
//   * Hub                → centered standalone <Loader2/> spinner
//   * PipelineRunner     → button-interior <Loader2/> only
//   * Briefing / Gates   → inline <Loader2/> + "Loading…" text
//   * DownloadCenter     → button-interior spinner; tiles flicker in
//   * Inspector          → no skeleton, just empty cards
//
// The audit (§8.5) flagged this as a high-severity hit because users
// see five different "loading" affordances depending on which pane
// they happen to land on. We unify around three primitives:
//
//   * ``<Skeleton>``         — generic shimmer block (rounded, dark)
//   * ``<SkeletonCard>``     — ``alm-card`` slab pre-populated with
//     stacked shimmer lines, used for "we know a card lives here, the
//     content is loading" surfaces (Inspector tabs, Decision Log).
//   * ``<SkeletonRow>``      — flex row with an icon column + text
//     stack, used for artifact rows / phase rows that haven't loaded
//     yet (PipelineRunner, DownloadCenter tile grid).
//
// The shimmer animation is CSS-only (see ``index.css :: alm-skeleton``)
// so it runs on first paint without JS overhead.

import type { HTMLAttributes } from 'react'

import { cn } from '@/lib/utils'

export function Skeleton({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden
      className={cn('alm-skeleton rounded-md', className)}
      {...rest}
    />
  )
}

/**
 * Card-shaped skeleton that mirrors the ``alm-card`` slab. ``lines``
 * controls how many shimmer rows render below the optional title bar.
 */
export function SkeletonCard({
  className,
  showTitle = true,
  lines = 3,
}: {
  className?: string
  showTitle?: boolean
  lines?: number
}) {
  return (
    <div
      role="status"
      aria-label="Loading…"
      className={cn('alm-card space-y-2', className)}
    >
      {showTitle ? <Skeleton className="h-3 w-1/3" /> : null}
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-3',
            i === lines - 1 ? 'w-2/3' : i % 2 === 0 ? 'w-full' : 'w-5/6',
          )}
        />
      ))}
    </div>
  )
}

/**
 * Single horizontal row mirroring an artifact / phase / file tile.
 * ``height`` matches the full-row layout PipelineRunner / DownloadCenter
 * use so the skeleton occupies the same space as the loaded content.
 */
export function SkeletonRow({
  className,
  height = 56,
}: {
  className?: string
  height?: number
}) {
  return (
    <div
      role="status"
      aria-label="Loading…"
      style={{ minHeight: height }}
      className={cn(
        'flex items-center gap-3 rounded-md border border-slate-700/60 bg-slate-900/40 px-3 py-2',
        className,
      )}
    >
      <Skeleton className="h-7 w-7 rounded" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-2.5 w-1/3" />
      </div>
      <Skeleton className="h-5 w-16 rounded-full" />
    </div>
  )
}
